import { ListTodo } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TaskStatusEvent } from "@/lib/render-items";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  isTaskStatus,
  TASK_STATUS_LABEL_KEYS,
} from "@/lib/task-status";
import { cn } from "@/lib/utils";

function fallbackStatusLabel(status: string): string {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

interface TaskStatusEventBannerProps {
  event: TaskStatusEvent;
  className?: string;
}

/** Render an agent-triggering task transition as a system event, not a chat bubble. */
export function TaskStatusEventBanner({
  event,
  className,
}: TaskStatusEventBannerProps) {
  const { t } = useTranslation();
  const displayStatus = (status: string) => (
    isTaskStatus(status) ? t(TASK_STATUS_LABEL_KEYS[status]) : fallbackStatusLabel(status)
  );

  return (
    <Alert
      className={cn(
        "mx-4 mb-4 w-auto border-primary/20 bg-primary/5 py-2.5 [&>svg]:top-1/2 [&>svg]:-translate-y-1/2",
        className,
      )}
      data-testid="task-status-event"
      role="status"
    >
      <ListTodo className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
      <AlertDescription className="min-w-0 overflow-hidden break-words text-foreground">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <AlertTitle className="mb-0">{t("projects:taskStatusChanged")}</AlertTitle>
          <span className="min-w-0 break-words text-sm text-muted-foreground">
            {displayStatus(event.oldStatus)} → {displayStatus(event.newStatus)}
          </span>
        </div>
      </AlertDescription>
    </Alert>
  );
}
